/**
 * games/index.tsx — Public game catalog.
 *
 * Lives at `/games`. Renders <HeroBanner> + three catalog sections:
 *   1. Game Publik (3) — ready to play without login (free-play funnel)
 *   2. Kelas Coding (7) — ready classroom games, gated by class access
 *   3. Sedang dikembangkan (21) — planned/in-progress roadmap
 *
 * The 3 public games are the SD-1 entry-level trio (sequence, instructions,
 * patterns) so first-time visitors can sample the curriculum before
 * joining a class. Classroom games need a tutor invitation — they show
 * the locked card with a "Gabung kelas" CTA. Future games stay visible
 * with an explicit "In progress" label so the roadmap is transparent.
 *
 * The catalog reads the static `publicCatalogQuery` (which fetches
 * `/registry.json`) instead of `/api/games` so visitors don't need a
 * session. The API mirrors the same payload once Fase 4+ wires D1.
 *
 * No bottom CTA strip — this is an internal portal surface, not a
 * marketing page. Cross-links to /worksheets live in the navbar.
 */

import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { HeroBanner } from '../../components/public/HeroBanner.js';
import { GameCardPublic } from '../../components/public/GameCardPublic.js';
import { StatePanel } from '../../components/ui/StatePanel.js';
import {
  publicCatalogQuery,
  catalogStateQuery,
  sessionQuery,
  myClassContentQuery,
  mergeCatalogWithState,
  type GameSummary,
} from '../../lib/api/queries.js';

export const Route = createFileRoute('/games/')({
  component: GamesCatalogPage,
});

function GamesCatalogPage(): ReactNode {
  const catalog = useQuery(publicCatalogQuery);
  const state = useQuery(catalogStateQuery);
  const session = useQuery(sessionQuery);

  const user = session.data?.ok ? session.data.data?.user : null;
  const isLoggedIn = !!user;
  // SessionPrincipal.role covers admin/tutor/player/parent; SessionPrincipal.kind
  // is the legacy student/player/parent token. We treat both so a `kind: 'student'`
  // (player) is correctly distinguished from a `role: 'tutor'`. Default to
  // 'visitor' when the catalog is rendered without a session.
  const viewerRole: 'admin' | 'tutor' | 'student' | 'parent' | 'visitor' = user
    ? user.role === 'admin'
      ? 'admin'
      : user.role === 'tutor'
        ? 'tutor'
        : user.role === 'parent'
          ? 'parent'
          : 'student'
    : 'visitor';
  const stateMap = state.data?.ok ? (state.data.data?.games ?? {}) : {};
  const games: GameSummary[] = mergeCatalogWithState(
    catalog.data?.games ?? [],
    stateMap,
    'gameId',
  );
  // N-13 Opsi A: game yang dibuka tutor untuk kelas siswa dapat pill
  // "Dibuka tutor". Query hanya jalan untuk sesi login (guest = 401).
  const classContent = useQuery({ ...myClassContentQuery, enabled: isLoggedIn });
  const openedGameIds = new Set<string>(
    isLoggedIn
      ? (classContent.data?.classes ?? []).flatMap((c) => c.games.map((g) => g.gameId))
      : [],
  );
  const publicGames = games.filter(
    (g) => g.status === 'ready' && g.visibility === 'public',
  );
  const classroomGames = games.filter(
    (g) => g.status === 'ready' && g.visibility === 'classroom',
  );
  const futureGames = games.filter((g) => g.status !== 'ready');

  return (
    <main className="page-shell">
      <HeroBanner
        eyebrow="Katalog game"
        title="Pilih game yang siap dimainkan"
        description={`${publicGames.length} game siap dimainkan gratis — cocok untuk siswa yang baru mulai. ${classroomGames.length} game tambahan terbuka setelah kamu gabung kelas Coding Class. Program ini invitation-only — minta kode undangan 6-karakter ke tutor kamu, atau daftar sebagai siswa untuk membuka seluruh game.`}
        primaryAction={
          isLoggedIn ? null : (
            <a className="button button-primary button-lg" href="/login">
              Masuk dengan kode undangan
            </a>
          )
        }
        heroArtUrl="/images/generated/games-hero/games-hero.png"
        heroArtAlt="Ilustrasi katalog game Coding Class: kartu browser dengan tile kode warna-warni, gamepad, dan bintang emas"
      />

      <section
        className="section"
        aria-labelledby="ready-games"
        id="ready-games"
        data-testid="public-games-section"
      >
        <h2 className="section-title" id="ready-games">
          Game Publik ({publicGames.length})
        </h2>
        <p className="form-message form-message-muted">
          Trio pengantar untuk siswa yang baru mulai — algoritma, instruksi arah, dan pola. Tanpa login.
        </p>
        {catalog.isLoading ? (
          <p className="form-message">Memuat katalog…</p>
        ) : catalog.isError ? (
          <StatePanel
            variant="error"
            title="Katalog tidak dapat dimuat"
            description="Coba segarkan halaman ini."
          />
        ) : publicGames.length > 0 ? (
          <div className="card-grid">
            {publicGames.map((game) => (
              <GameCardPublic
                key={game.gameId}
                game={game}
                isLoggedIn={isLoggedIn}
                viewerRole={viewerRole}
                classOpened={openedGameIds.has(game.gameId)}
              />
            ))}
          </div>
        ) : (
          <StatePanel
            variant="empty"
            title="Belum ada game publik"
            description="Game pertama sedang disiapkan."
          />
        )}
      </section>

      <section
        className="section"
        aria-labelledby="classroom-games"
        data-testid="classroom-games-section"
      >
        <h2 className="section-title" id="classroom-games">
          Kelas Coding ({classroomGames.length})
        </h2>
        <p className="form-message form-message-muted">
          Kurikulum lanjutan untuk siswa yang sudah gabung kelas. Akses dibuka oleh tutor lewat
          undangan per kelas — bukan per siswa.
        </p>
        {classroomGames.length > 0 ? (
          <div className="card-grid">
            {classroomGames.map((game) => (
              <GameCardPublic
                key={game.gameId}
                game={game}
                isLoggedIn={isLoggedIn}
                viewerRole={viewerRole}
                classOpened={openedGameIds.has(game.gameId)}
              />
            ))}
          </div>
        ) : (
          <StatePanel
            variant="empty"
            title="Belum ada game kelas"
            description="Game kelas akan ditambahkan bertahap."
          />
        )}
      </section>

      <section
        className="section"
        aria-labelledby="in-progress-games"
        data-testid="in-progress-games-section"
      >
        <h2 className="section-title" id="in-progress-games">
          Sedang dikembangkan ({futureGames.length})
        </h2>
        <p className="form-message form-message-muted">
          Setiap game di bawah ini masih dalam tahap pengembangan dan akan hadir bertahap.
        </p>
        {futureGames.length > 0 ? (
          <div className="card-grid">
            {futureGames.map((game) => (
              <GameCardPublic
                key={game.gameId}
                game={game}
                isLoggedIn={isLoggedIn}
                viewerRole={viewerRole}
                classOpened={openedGameIds.has(game.gameId)}
              />
            ))}
          </div>
        ) : (
          <StatePanel
            variant="empty"
            title="Tidak ada game yang sedang dikembangkan"
            description="Katalog sedang diperbarui."
          />
        )}
      </section>
    </main>
  );
}
